import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import {Dictionary} from 'highcharts';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {UtilsService} from '../../../services/utils/utils.service';
import moment from 'moment';

export interface Field {
  name: string;
  label: string;
  labelResolver?: (form: UntypedFormGroup) => string;
  fieldType: FieldType;
  values?: Array<string>;
  valuesResolver?: (form: UntypedFormGroup) => Array<string>;
  valuesLoader?: (form: UntypedFormGroup, searchValue?: string) => Promise<Array<string>>;
  valuesLoaderDependencies?: string[];
  searchable?: boolean;
  reloadValuesOnSearch?: boolean;
  searchPlaceholder?: string;
  searchPlaceholderResolver?: (form: UntypedFormGroup) => string;
  actionIcon?: string;
  actionDisabledResolver?: (form: UntypedFormGroup) => boolean;
  action?: (form: UntypedFormGroup) => void;
  pattern?: string;
  required?: boolean;
  readOnly?: boolean;
  multiple?: boolean;
  minDate?: string;
  maxDate?: string;
  dateCutoffField?: string;
  invisible?: (arg0: any) => boolean;
  hidePassword?: boolean;
  validateJson?: ValidatorFn;
  validators?: ValidatorFn[];
  errorMessages?: Record<string, string>;
  placeholder?: string;
  hint?: string;
  help?: FieldHelp;
  sectionTitle?: string;
}

export interface FieldHelp {
  title: string;
  description?: string;
  groups: FieldHelpGroup[];
}

export interface FieldHelpGroup {
  title: string;
  fields: string[];
}

export enum FieldType {
  F_STRING,
  F_NUMBER,
  F_PASTDATE,
  F_FUTUREDATE,
  F_ANYDATE,
  F_EMAIL,
  F_DROPDOWN,
  F_PHONE,
  F_SSN,
  F_STATE,
  F_JSON,
  F_TOGGLE,
  F_COLOR,
  F_IMAGE,
  F_HIDDEN_DATA,
  F_CURRENCY,
  F_PRIMARY_INSURANCE_CUSTOM_FIELDS,
  F_ACTION_BUTTON
}

interface EditablePrimaryInsuranceCustomField {
  fieldName: string;
  label: string;
  type: string;
  optionsText: string;
}

@Component({
  selector: 'app-member-addedit',
  templateUrl: './member-addedit.component.html',
  styleUrls: ['./member-addedit.component.scss']
})
export class MemberAddeditComponent implements OnInit, OnDestroy {
  public fieldsOutsideMatFormField = [
    FieldType.F_TOGGLE,
    FieldType.F_IMAGE,
    FieldType.F_PRIMARY_INSURANCE_CUSTOM_FIELDS,
    FieldType.F_ACTION_BUTTON,
  ];

  protected form!: UntypedFormGroup;
  protected fieldType = FieldType;
  protected errorMessage = '';
  protected validationMessages: string[] = [];
  protected primaryInsuranceCustomFieldTypes = [
    {label: 'Text', value: 'string'},
    {label: 'Number', value: 'number'},
    {label: 'Yes / No', value: 'boolean'},
    {label: 'Date', value: 'date'},
    {label: 'Dropdown', value: 'dropdown'},
  ];

  private hasSubmitted = false;
  private primaryInsuranceCustomFieldRows: Record<string, EditablePrimaryInsuranceCustomField[]> = {};
  private dropdownSearchControls: Record<string, UntypedFormControl> = {};
  private resolvedDropdownValues: Record<string, Array<string>> = {};
  private dropdownOptionCache: Record<string, Record<string, string>> = {};
  private dropdownLoadTokens: Record<string, number> = {};
  private dropdownSearchDebounceHandles: Record<string, ReturnType<typeof setTimeout>> = {};
  private fieldValidators: Record<string, ValidatorFn[]> = {};

  constructor(
    private formBuilder: UntypedFormBuilder,
    public dialogRef: MatDialogRef<MemberAddeditComponent>,
    private utilsService: UtilsService,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: MemberAddeditDialogModel,
  ) {
    this.data = data;
    const controls: Dictionary<any> = {};
    this.data.fields.forEach(field => {
      const validators: ValidatorFn[] = [];
      switch (field.fieldType) {
        case FieldType.F_EMAIL:
          validators.push(Validators.email);
          break;

        case FieldType.F_CURRENCY:
          validators.push(Validators.pattern(/^(?:0|[1-9]\d*)(?:\.\d{2})?$/));
          break;

        case FieldType.F_JSON:
        case FieldType.F_PRIMARY_INSURANCE_CUSTOM_FIELDS:
          if (field?.validateJson) {
            validators.push(field.validateJson);
          }
          break;

        case FieldType.F_SSN:
          field.hidePassword = true;
          break;

        case FieldType.F_PASTDATE: {
          const today = moment().add(1, 'days');
          const hundredYearsAgo = moment().subtract(100, 'years');
          field.minDate = hundredYearsAgo.format('YYYY-MM-DD');
          field.maxDate = today.format('YYYY-MM-DD');

          if (this.data.data) {
            this.data.data[field.name] = moment(this.data.data[field.name]).toISOString();
          }
          break;
        }

        case FieldType.F_ANYDATE: {
          const today = new Date();
          const hundredYearsAgo = moment().subtract(100, 'years');
          const oneYearInTheFuture = new Date();
          field.minDate = hundredYearsAgo.format('YYYY-MM-DD');
          oneYearInTheFuture.setFullYear(today.getFullYear() + 1);
          field.maxDate = oneYearInTheFuture.toISOString().split('T')[0];
          break;
        }

        case FieldType.F_FUTUREDATE: {
          const today = new Date();
          const oneYearInTheFuture = new Date();
          oneYearInTheFuture.setFullYear(today.getFullYear() + 1);
          field.maxDate = oneYearInTheFuture.toISOString().split('T')[0];
          field.minDate =
              field.dateCutoffField && this.data.data
                  ? this.data.data[
                  this.data.fields.find(
                      thisField => thisField.name === field.dateCutoffField
                  )?.name || 0
                      ]
                  : today.toISOString().split('T')[0];

          if (this.data.data) {
            this.data.data[field.name] = moment(this.data.data[field.name]).toISOString();
          }
          break;
        }
      }

// Non-fieldType-based rules
      if (field.pattern) {
        validators.push(Validators.pattern(field.pattern));
      }
      if (field.validators) {
        validators.push(...field.validators);
      }

      if (field.fieldType === FieldType.F_PRIMARY_INSURANCE_CUSTOM_FIELDS) {
        this.primaryInsuranceCustomFieldRows[field.name] = this.buildPrimaryInsuranceCustomFieldRows(
          this.data.data ? this.data.data[field.name] : null
        );
      }

      this.fieldValidators[field.name] = validators;
      controls[field.name] = new UntypedFormControl(
        {
          value: field.fieldType === FieldType.F_PRIMARY_INSURANCE_CUSTOM_FIELDS
            ? this.serializePrimaryInsuranceCustomFieldRows(this.primaryInsuranceCustomFieldRows[field.name] || [])
            : this.data.data ? this.data.data[field.name] : null,
          disabled: field.readOnly && ![FieldType.F_JSON, FieldType.F_PRIMARY_INSURANCE_CUSTOM_FIELDS].includes(field.fieldType),
        },
        validators
      );
    });

    this.form = this.formBuilder.group(controls);
    this.syncFieldValidators();
    if (this.data?.validateOnInit) {
      this.refreshValidationErrors();
      this.form.valueChanges.subscribe(changes => {
        this.refreshValidationErrors();
      });
    }
  }

  ngOnInit(): void {
    this.data.fields.forEach(field => {
      if (field.fieldType === FieldType.F_DROPDOWN && field.searchable && !this.dropdownSearchControls[field.name]) {
        this.dropdownSearchControls[field.name] = new UntypedFormControl('');
        if (field.reloadValuesOnSearch && field.valuesLoader) {
          this.dropdownSearchControls[field.name].valueChanges.subscribe(() => this.scheduleDropdownValuesLoad(field));
        }
      }
      if (field.fieldType === FieldType.F_DROPDOWN && field.valuesLoader) {
        this.loadDropdownValues(field);
        (field.valuesLoaderDependencies || []).forEach(dependency => {
          this.form.get(dependency)?.valueChanges.subscribe(() => {
            const control = this.form.get(field.name);
            this.dropdownSearchControls[field.name]?.setValue('', { emitEvent: false });
            control?.setValue(field.multiple ? [] : '', { emitEvent: false });
            control?.markAsTouched();
            this.refreshValidationErrors();
            this.loadDropdownValues(field);
          });
        });
      }
    });
    this.syncResolvedDropdownValues();
    this.form.valueChanges.subscribe(() => this.syncResolvedDropdownValues());
  }

  ngOnDestroy(): void {
    Object.keys(this.dropdownSearchDebounceHandles).forEach(fieldName => {
      clearTimeout(this.dropdownSearchDebounceHandles[fieldName]);
    });
    this.dropdownSearchDebounceHandles = {};
  }

  okClicked(): void {
    // tslint:disable-next-line:forin
    /*
    for (const key in this.form.value) {
      const dateField = this.data.fields.find(field => field.name === key && (field.fieldType === FieldType.F_FUTUREDATE || field.fieldType === FieldType.F_PASTDATE));
      if (dateField) {
        console.log(key, this.form.value[key])
        this.form.value[key] = this.form.value[key].split('T')[0];
      }
    }
     */
    if (!this.hasSubmitted && !this.data?.validateOnInit) {
      this.utilsService.getFormErrors(this.form, errorMessage => this.errorMessage = errorMessage, this.data.fields);
      this.validationMessages = this.collectValidationMessages();
      this.form.valueChanges.subscribe(changes => {
        this.refreshValidationErrors();
      });
      this.hasSubmitted = true;
    }
    this.syncFieldValidators();
    if (this.form.invalid) {
      this.refreshValidationErrors();
      return;
    }
    this.dialogRef.close(this.form.value);
  }

  onDismiss(): void {
    this.dialogRef.close(null);
  }

  getElementById(id: string): any {
    return document.getElementById(id);
  }

  imageFileChange($event: any, fieldName: string): void {
    if ($event && $event.target.files[0] && $event.target.files[0].type.indexOf('image') < 0) {
      console.log('Invalid file type selected. Must be image.');
    }
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.form.controls[`${fieldName}ImageDataSrc`].setValue(e.target.result);
    };
    if ($event.target.files[0]) {
      this.form.controls[`${fieldName}ImageData`].setValue($event.target.files[0]);
    }
    reader.readAsDataURL($event.target.files[0]);
  }

  clearImageField(fieldName: string): void {
    if (this.form.controls[fieldName]) {
      this.form.controls[fieldName].setValue('');
    }
    if (this.form.controls[`${fieldName}ImageData`]) {
      this.form.controls[`${fieldName}ImageData`].setValue(null);
    }
    if (this.form.controls[`${fieldName}ImageDataSrc`]) {
      this.form.controls[`${fieldName}ImageDataSrc`].setValue('');
    }

    const input = document.getElementById(`${fieldName}FileInput`) as HTMLInputElement | null;
    if (input) {
      input.value = '';
    }
  }

  getPrimaryInsuranceCustomFieldRows(fieldName: string): EditablePrimaryInsuranceCustomField[] {
    return this.primaryInsuranceCustomFieldRows[fieldName] || [];
  }

  addPrimaryInsuranceCustomFieldRow(fieldName: string): void {
    const rows = [
      ...this.getPrimaryInsuranceCustomFieldRows(fieldName),
      {fieldName: '', label: '', type: 'string', optionsText: ''}
    ];
    this.setPrimaryInsuranceCustomFieldRows(fieldName, rows);
  }

  removePrimaryInsuranceCustomFieldRow(fieldName: string, index: number): void {
    const rows = [...this.getPrimaryInsuranceCustomFieldRows(fieldName)];
    rows.splice(index, 1);
    this.setPrimaryInsuranceCustomFieldRows(fieldName, rows);
  }

  updatePrimaryInsuranceCustomField(
    fieldName: string,
    index: number,
    key: keyof EditablePrimaryInsuranceCustomField,
    value: string
  ): void {
    const rows = this.getPrimaryInsuranceCustomFieldRows(fieldName).map((row, currentIndex) => {
      if (currentIndex !== index) {
        return {...row};
      }

      const updatedRow = {...row, [key]: value};
      if (key === 'type' && value !== 'dropdown') {
        updatedRow.optionsText = '';
      }
      return updatedRow;
    });
    this.setPrimaryInsuranceCustomFieldRows(fieldName, rows);
  }

  trackByIndex(index: number): number {
    return index;
  }

  getFieldValues(field: Field): Array<string> {
    if (field.valuesLoader) {
      return this.withSelectedDropdownValues(field, this.resolvedDropdownValues[field.name] || []);
    }
    return field.valuesResolver ? field.valuesResolver(this.form) : (field.values || []);
  }

  getFilteredFieldValues(field: Field): Array<string> {
    const values = this.getFieldValues(field);
    if (!field.searchable) {
      return values;
    }

    const searchValue = `${this.dropdownSearchControls[field.name]?.value || ''}`.trim().toLowerCase();
    if (!searchValue) {
      return values;
    }

    return values.filter(value => {
      const [label, rawValue] = value.includes(':') ? value.split(':', 2) : [value, value];
      return label.toLowerCase().includes(searchValue) || rawValue.toLowerCase().includes(searchValue);
    });
  }

  getDropdownSearchControl(fieldName: string): UntypedFormControl {
    if (!this.dropdownSearchControls[fieldName]) {
      this.dropdownSearchControls[fieldName] = new UntypedFormControl('');
    }
    return this.dropdownSearchControls[fieldName];
  }

  getFieldLabel(field: Field): string {
    return field.labelResolver ? field.labelResolver(this.form) : field.label;
  }

  getSearchPlaceholder(field: Field): string {
    return field.searchPlaceholderResolver
      ? field.searchPlaceholderResolver(this.form)
      : field.searchPlaceholder || 'Search...';
  }

  runFieldAction(field: Field): void {
    field.action?.(this.form);
    this.refreshValidationErrors();
  }

  isFieldActionDisabled(field: Field): boolean {
    return field.actionDisabledResolver ? field.actionDisabledResolver(this.form) : false;
  }

  protected isOkDisabled(): boolean {
    return this.validationMessages.length > 0 || (this.data.disableOkWhenInvalid && this.form.invalid);
  }

  private refreshValidationErrors(): void {
    this.syncFieldValidators();
    if (this.data?.validateOnInit || this.hasSubmitted) {
      this.utilsService.getFormErrors(this.form, errorMessage => this.errorMessage = errorMessage, this.data.fields);
      this.validationMessages = this.collectValidationMessages();
    }
  }

  private collectValidationMessages(): string[] {
    return this.data.fields.reduce((messages: string[], field) => {
      if (field.invisible?.(this.form)) {
        return messages;
      }

      const control = this.form.get(field.name);
      if (!control?.errors) {
        return messages;
      }

      Object.keys(control.errors).forEach(errorKey => {
        messages.push(this.getFieldErrorMessage(field, errorKey));
      });
      return messages;
    }, []);
  }

  private getFieldErrorMessage(field: Field, errorKey: string): string {
    if (field.errorMessages?.[errorKey]) {
      return field.errorMessages[errorKey];
    }

    const label = this.getFieldLabel(field);
    if (errorKey === 'required') {
      return `${label} is required.`;
    }
    if (errorKey === 'email') {
      return `${label} must be a valid email address.`;
    }
    if (errorKey === 'currency') {
      return `${label} must be a valid amount.`;
    }
    return `${label} is invalid.`;
  }

  private syncFieldValidators(): void {
    this.data.fields.forEach(field => {
      const control = this.form.get(field.name);
      if (!control) {
        return;
      }

      if (field.invisible?.(this.form)) {
        control.clearValidators();
        control.updateValueAndValidity({ emitEvent: false });
        return;
      }

      const validators = [...(this.fieldValidators[field.name] || [])];
      if (field.required) {
        validators.unshift(Validators.required);
      }
      control.setValidators(validators);
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  refreshResolvedDropdownValues(): void {
    this.data.fields
      .filter(field => field.fieldType === FieldType.F_DROPDOWN && !!field.valuesLoader)
      .forEach(field => this.loadDropdownValues(field));
    this.syncResolvedDropdownValues();
    this.changeDetectorRef.detectChanges();
  }

  private async loadDropdownValues(field: Field): Promise<void> {
    if (!field.valuesLoader) {
      return;
    }

    const token = (this.dropdownLoadTokens[field.name] || 0) + 1;
    this.dropdownLoadTokens[field.name] = token;

    try {
      const values = await field.valuesLoader(this.form, this.getDropdownValuesLoaderSearchValue(field));
      if (this.dropdownLoadTokens[field.name] !== token) {
        return;
      }

      this.resolvedDropdownValues[field.name] = values;
      this.cacheDropdownValues(field.name, values);
      this.syncResolvedDropdownValues();
      this.changeDetectorRef.detectChanges();
    } catch (error) {
      console.error(error);
      if (this.dropdownLoadTokens[field.name] === token) {
        this.resolvedDropdownValues[field.name] = [];
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  private scheduleDropdownValuesLoad(field: Field): void {
    if (this.dropdownSearchDebounceHandles[field.name]) {
      clearTimeout(this.dropdownSearchDebounceHandles[field.name]);
    }

    this.dropdownSearchDebounceHandles[field.name] = setTimeout(() => {
      delete this.dropdownSearchDebounceHandles[field.name];
      void this.loadDropdownValues(field);
    }, 300);
  }

  private getDropdownValuesLoaderSearchValue(field: Field): string {
    if (!field.reloadValuesOnSearch) {
      return '';
    }

    return `${this.dropdownSearchControls[field.name]?.value || ''}`.trim();
  }

  private withSelectedDropdownValues(field: Field, values: string[]): string[] {
    const selectedValues = this.getSelectedDropdownValues(field);
    if (!selectedValues.length) {
      return values;
    }

    const valuesByRawValue = values.reduce((result: Record<string, string>, option) => {
      result[this.getDropdownRawValue(option)] = option;
      return result;
    }, {});
    const selectedOptions = selectedValues
      .filter(value => !valuesByRawValue[value])
      .map(value => this.dropdownOptionCache[field.name]?.[value] || `${value}:${value}`);

    return selectedOptions.length ? [...values, ...selectedOptions] : values;
  }

  private getSelectedDropdownValues(field: Field): string[] {
    const value = this.form.get(field.name)?.value;
    if (field.multiple) {
      return Array.isArray(value) ? value.filter(item => !!item) : [];
    }
    return value ? [value] : [];
  }

  private cacheDropdownValues(fieldName: string, values: string[]): void {
    if (!this.dropdownOptionCache[fieldName]) {
      this.dropdownOptionCache[fieldName] = {};
    }

    values.forEach(option => {
      this.dropdownOptionCache[fieldName][this.getDropdownRawValue(option)] = option;
    });
  }

  private getDropdownRawValue(option: string): string {
    return option.split(':')[1] || option;
  }

  private setPrimaryInsuranceCustomFieldRows(
    fieldName: string,
    rows: EditablePrimaryInsuranceCustomField[],
  ): void {
    this.primaryInsuranceCustomFieldRows[fieldName] = rows.map(row => ({...row}));

    const control = this.form?.get(fieldName);
    if (!control) {
      return;
    }

    control.setValue(this.serializePrimaryInsuranceCustomFieldRows(rows));
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity();
  }

  private buildPrimaryInsuranceCustomFieldRows(value: any): EditablePrimaryInsuranceCustomField[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((customField: any) => ({
      fieldName: customField?.fieldName || '',
      label: customField?.label || '',
      type: customField?.type || 'string',
      optionsText: Array.isArray(customField?.options) ? customField.options.join(', ') : ''
    }));
  }

  private serializePrimaryInsuranceCustomFieldRows(rows: EditablePrimaryInsuranceCustomField[]): any[] {
    return rows.map(row => {
      const serializedRow: any = {
        fieldName: row.fieldName?.trim() || '',
        label: row.label?.trim() || '',
        type: row.type || 'string'
      };

      if (serializedRow.type === 'dropdown') {
        serializedRow.options = (row.optionsText || '')
          .split(',')
          .map(option => option.trim())
          .filter(option => !!option);
      }

      return serializedRow;
    });
  }

  private syncResolvedDropdownValues(): void {
    this.data.fields.forEach(field => {
      if (field.fieldType !== FieldType.F_DROPDOWN || !field.valuesResolver) {
        return;
      }

      const control = this.form.get(field.name);
      if (!control) {
        return;
      }

      const values = this.getFieldValues(field);
      if (field.valuesLoader && !this.resolvedDropdownValues[field.name]) {
        return;
      }

      const allowedValues = values.map(value => value.split(':')[1] || value);
      if (!allowedValues.length) {
        if (control.value) {
          control.setValue(field.multiple ? [] : '', { emitEvent: false });
        }
        return;
      }

      if (field.multiple) {
        const currentValues = Array.isArray(control.value) ? control.value : [];
        const filteredValues = currentValues.filter(value => allowedValues.includes(value));
        if (filteredValues.length !== currentValues.length) {
          control.setValue(filteredValues, { emitEvent: false });
        }
        return;
      }

      if (!allowedValues.includes(control.value)) {
        control.setValue(allowedValues[0], { emitEvent: false });
      }
    });
  }

}

export class MemberAddeditDialogModel {
  constructor(
    public data: Dictionary<any> | null,
    public fields: Array<Field>,
    public title: string,
    public validateOnInit: boolean = true,
    public titlePrefix?: 'Add' | 'Edit' | 'View',
    public disableOkWhenInvalid: boolean = false,
  ) {
  }
}
